import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import CustomValidators from '../../../../shared/custom-validators';
import { CustomerCampaignService } from '../../../../services/customer-campaign.service';
import { TrackingKeywordSuggestion, KeywordSuggestionFilter, KeywordSuggestion, KeywordSuggestionSources, PageSuggestion, KeywordConfig } from '../../../../shared/models/keywords/keywords';
import { KeywordResearchService } from '../../../../services/keyword-research.service';
import Utils from '../../../../shared/utils';
import { map } from 'rxjs';

@Component({
  selector: 'app-components-shared-keyword-research-add-tracking-keyword',
  templateUrl: './add-tracking-keyword.component.html',
  styleUrls: ['../keyword-research/keyword-research.component.scss']
})
export class AddTrackingKeywordComponent implements OnInit {
  @Input() customer: Boo.Objects.Customer;
  @Input() pageSuggestions: PageSuggestion[];
  @Input() trackingKeywordSuggestions: TrackingKeywordSuggestion[];
  @Input() keywordConfig: KeywordConfig;

  @ViewChild('keywordInput') keywordInput: ElementRef;
  @ViewChild('areaInput') areaInput: ElementRef;
  @ViewChild('fileInput') fileInput: ElementRef;

  keywordSuggestionFilter: KeywordSuggestionFilter;
  keywordControl: FormControl<string>;
  areaControl: FormControl<string>;
  formGroup: FormGroup;
  addingKeyword = false;
  isAreaFirst = false;
  rankThreshold: number;
  defaultMaximumTrackingKeywords = 100;

  localCampaignCsvFormat = this.customerCampaignService.localCampaignCsvFormat;
  localCampaignExampleCsv = this.customerCampaignService.localCampaignExampleCsv;
  nationalCampaignCsvFormat = this.customerCampaignService.nationalCampaignCsvFormat;
  nationalCampaignExampleCsv = this.customerCampaignService.nationalCampaignExampleCsv;

  constructor(
    private customerCampaignService: CustomerCampaignService,
    private keywordResearchService: KeywordResearchService) { }

  ngOnInit(): void {
    this.rankThreshold = this.keywordResearchService.rankThreshold;
    this.setupForm();
  }

  toggleAddingKeyword(): void {
    this.addingKeyword = !this.addingKeyword;

    if (this.addingKeyword) {
      this.isAreaFirst = false;
      this.keywordControl.reset('');
      this.areaControl.reset('');
      this.focus();
    }
  }

  setupForm(): void {
    this.keywordControl = new FormControl<string>('', [
      Validators.required,
      Validators.maxLength(128),
      CustomValidators.mustNotBeWhitespace()
    ]);
    this.areaControl = new FormControl<string>('', [
      CustomValidators.requiredIfValidator(() => !this.customer.IsCampaignNational),
      Validators.maxLength(256),
      CustomValidators.mustNotBeWhitespace()
    ]);
    this.formGroup = new FormGroup({ keyword: this.keywordControl, area: this.areaControl }, {
      validators: [CustomValidators.keywordsCannotBeDuplicated(this.customerCampaignService, () => this.isAreaFirst, this.getExistingKeywordPhrases.bind(this), 'This keyword already exists and cannot be duplicated.')]
    });
  }

  getExistingKeywordPhrases(): string[] {
    return this.trackingKeywordSuggestions
      .filter(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational)
      .map(x => x.KeywordPhrase.toLowerCase().trim());
  }

  keywordSort = (a: KeywordSuggestion, b: KeywordSuggestion): number => this.keywordResearchService.keywordSort(a, b);

  addKeyword(): void {
    this.keywordControl.markAsDirty();
    this.keywordControl.updateValueAndValidity();

    if (!this.customer.IsCampaignNational) {
      this.areaControl.markAsDirty();
      this.areaControl.updateValueAndValidity();
    } else {
      this.areaControl.reset('');
      this.isAreaFirst = false;
    }

    if (!this.formGroup.valid) {
      toastr.error("Please correct errors before adding a keyword.");
      return;
    }

    this.addKeywordSuggestionValues(this.keywordControl.value, this.areaControl.value);

    this.toggleAddingKeyword();
  }

  private addKeywordSuggestionValues(keyword: string, area: string): void {
    const phrase = this.customerCampaignService.getKeywordPhrase(keyword, area, this.isAreaFirst);

    const existingTrackingKeywordSuggestion = this.trackingKeywordSuggestions.filter(x => x.IsCampaignNational === this.customer.IsCampaignNational)
      .find(x => x.KeywordPhrase.toLowerCase().trim() === phrase.toLowerCase().trim());

    const existingKeywordSuggestion = this.pageSuggestions.filter(x => x.IsCampaignNational === this.customer.IsCampaignNational)
      .flatMap(x => x.KeywordSuggestions)
      .find(x => x.KeywordPhrase.toLowerCase().trim() === phrase.toLowerCase().trim() && x.IsCampaignNational === this.customer.IsCampaignNational && x.SourceId === KeywordSuggestionSources.KeywordTool);

    if (existingTrackingKeywordSuggestion) {
      existingTrackingKeywordSuggestion.IsSelected = true;
      existingTrackingKeywordSuggestion.IsModified = true;
    } else if (existingKeywordSuggestion) {
      this.addKeywordSuggestion(existingKeywordSuggestion);
    } else {
      this.trackingKeywordSuggestions.push({
        TrackingKeywordSuggestionId: 0,
        CustomerId: this.customer.CustomerId,
        SourceId: KeywordSuggestionSources.User,
        IsCampaignNational: this.customer.IsCampaignNational,
        KeywordPhrase: phrase,
        Keyword: this.customerCampaignService.normalizeKeyword(keyword),
        Area: area ? this.customerCampaignService.normalizeArea(area) : null,
        IsAreaFirst: this.isAreaFirst,
        IsSelected: true,
        Volume: null,
        Competition: null,
        CompetitionDescription: null,
        Rank: null,
        InsertedDate: null,
        ArchivedDate: null,
        IsModified: true
      } as TrackingKeywordSuggestion);
    }
  }

  addKeywordSuggestion(keywordSuggestion: KeywordSuggestion): void {
    const existingTrackingKeywordSuggestion = this.trackingKeywordSuggestions.filter(x => !x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational)
      .find(x => x.KeywordPhrase.toLowerCase().trim() === keywordSuggestion.KeywordPhrase.toLowerCase().trim());

    if (existingTrackingKeywordSuggestion) {
      existingTrackingKeywordSuggestion.IsSelected = true;
      existingTrackingKeywordSuggestion.IsModified = true;
      existingTrackingKeywordSuggestion.Volume = keywordSuggestion.Volume;
      existingTrackingKeywordSuggestion.Competition = keywordSuggestion.Competition;
      existingTrackingKeywordSuggestion.CompetitionDescription = keywordSuggestion.CompetitionDescription;
      existingTrackingKeywordSuggestion.Rank = keywordSuggestion.Rank;
    } else {
      this.trackingKeywordSuggestions.push({
        TrackingKeywordSuggestionId: 0,
        CustomerId: this.customer.CustomerId,
        SourceId: keywordSuggestion.SourceId,
        IsCampaignNational: this.customer.IsCampaignNational,
        KeywordPhrase: keywordSuggestion.KeywordPhrase,
        Keyword: keywordSuggestion.Keyword,
        Area: keywordSuggestion.Area,
        IsAreaFirst: keywordSuggestion.IsAreaFirst,
        IsSelected: true,
        Volume: keywordSuggestion.Volume,
        Competition: keywordSuggestion.Competition,
        CompetitionDescription: keywordSuggestion.CompetitionDescription,
        Rank: keywordSuggestion.Rank,
        InsertedDate: null,
        ArchivedDate: null,
        IsModified: true
      } as TrackingKeywordSuggestion);
    }
  }

  addTrackingKeywordSuggestion(trackingKeywordSuggestion: TrackingKeywordSuggestion): void {
    trackingKeywordSuggestion.IsSelected = true;
    trackingKeywordSuggestion.IsModified = true;
  }

  openFileImport(): void {
    this.fileInput.nativeElement.click();
  }

  importKeywordSuggestions(event: any) {
    const file: File = event.target.files[0];
    this.fileInput.nativeElement.value = null;
    if (file) {
      const expectedCsvFormat = this.customer.IsCampaignNational ? this.nationalCampaignCsvFormat : this.localCampaignCsvFormat;
      Utils.parseCsv(file, expectedCsvFormat).pipe(map(results => results.map(row => { 
        const keyword = this.customerCampaignService.normalizeKeyword(row[0]);
        const area = row[1] ? this.customerCampaignService.normalizeArea(row[1]) : '';
        return {
          keyword: keyword,
          area: area,
          phrase: this.customerCampaignService.getKeywordPhrase(keyword, area, this.isAreaFirst)
        } as TrackingKeywordImport;
      }))).subscribe({
        next: (importValues) => {
          const maximumTrackingKeywords = this.keywordConfig?.MaximumTrackingKeywords || 100;
          const existingTrackingKeywordTotal = this.trackingKeywordSuggestions.filter(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational).length;
          let currentTrackingKeywordTotal = existingTrackingKeywordTotal;
          const uploadedTotal = importValues.length;
          const uniqueUploadedValues = Array.from(new Map(importValues.map(item => [item.phrase, item])).values());

          for (const value of uniqueUploadedValues) {
            if (currentTrackingKeywordTotal >= maximumTrackingKeywords) {
              break;
            }
            this.addKeywordSuggestionValues(value.keyword, value.area);
            currentTrackingKeywordTotal = this.trackingKeywordSuggestions.filter(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational).length;
          }

          const importedTotal = currentTrackingKeywordTotal - existingTrackingKeywordTotal;

          if (importedTotal === uploadedTotal) {
            toastr.success(`All ${ importedTotal } keywords imported.`);
          } else {
            toastr.warning(`${ importedTotal }/${ uploadedTotal } keywords imported.<br />${ uploadedTotal - importedTotal } keywords omitted due to duplicates or the tracking keyword maximum.`);
          }
        },
        error: (err: any) => toastr.error(err.message)
      });
    }
  }

  get maximumTrackingKeywordsAdded(): boolean {
    return this.trackingKeywordSuggestions.filter(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational).length >= (this.keywordConfig?.MaximumTrackingKeywords || this.defaultMaximumTrackingKeywords);
  }

  get availableKeywordSuggestions(): KeywordSuggestion[] {
    return this.pageSuggestions
      .filter(x => x.IsCampaignNational === this.customer.IsCampaignNational)
      .flatMap(x => x.KeywordSuggestions)
      .filter(x => x.SourceId === KeywordSuggestionSources.KeywordTool
        && !this.trackingKeywordSuggestions
          .filter(x => x.IsSelected && x.IsCampaignNational === this.customer.IsCampaignNational)
          .flatMap(x => x.KeywordPhrase).includes(x.KeywordPhrase))
      .reduce((acc, curr) => {
        if (!acc.set.has(curr.KeywordPhrase.toLowerCase().trim())) {
          acc.set.add(curr.KeywordPhrase.toLowerCase().trim());
          acc.list.push(curr);
        }
        return acc;
      }, { set: new Set<string>(), list: [] }).list;
  }

  get filteredKeywordSuggestions(): KeywordSuggestion[] {
    return this.keywordResearchService.filterKeywordSuggestions(this.availableKeywordSuggestions, this.keywordSuggestionFilter);
  }

  get filteredTrackingKeywordSuggestions(): TrackingKeywordSuggestion[] {
    return this.keywordResearchService.filterTrackingKeywordSuggestions(this.trackingKeywordSuggestions.filter(x => !x.IsSelected), this.keywordSuggestionFilter);
  }

  filterChanged($event: KeywordSuggestionFilter): void {
    this.keywordSuggestionFilter = $event;
  }

  private focus() {
    setTimeout(() => {
      this.keywordInput.nativeElement.focus();
    }, 0);
  }
}

interface TrackingKeywordImport {
  keyword: string;
  area?: string;
  phrase: string;
}